import React, { useReducer, useState, useEffect } from 'react';
import { render } from 'react-dom';
import { createGlobalStyle } from 'styled-components';
import {
  registerFrame,
  listen,
  MessageTypes,
  dispatchMessage
} from '../../utils/dispatcher';
import { filesReducer, setFiles } from './filesReducer';
import EmbedFrame from './EmbedFrame';
import { getConfig } from '../../utils/getConfig';
import { toOrigin } from '../../utils/toOrigin';
import { initialState } from '../IDE/reducers/files';

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
  }
`;

const App = () => {
  const [state, dispatch] = useReducer(filesReducer, [], initialState);
  const [isPlaying, setIsPlaying] = useState(false);
  const [basePath, setBasePath] = useState('');
  const [textOutput, setTextOutput] = useState(false);
  const [gridOutput, setGridOutput] = useState(false);
  // Stored AR slider values from the latest SKETCH message. Injected into the sketch
  // iframe at build time so its sliders load from Firebase (persist-by-default). Kept
  // out of EmbedFrame's rebuild deps — a live slider move must not restart the sketch.
  const [sliderValues, setSliderValues] = useState({});
  // Register the parent (editor) frame once, with a BARE origin as the postMessage target, and clean
  // up on unmount. Calling registerFrame in the render body leaked a new frame entry on every render
  // (App re-renders on every START/STOP/SKETCH), growing the dispatcher's frame map unboundedly.
  useEffect(() => {
    // EDITOR_URL is only set for production (separate Hosting sites). In dev editor and preview are
    // same-origin, so window.origin is the exact editor origin whatever host the page was opened
    // on (localhost or the LAN IP) — hardcoding localhost would drop messages on LAN access.
    const unsubscribe = registerFrame(
      window.parent,
      toOrigin(getConfig('EDITOR_URL', { warn: false }) ?? window.origin)
    );
    return unsubscribe;
  }, []);

  function handleMessageEvent(message) {
    const { type, payload } = message;
    switch (type) {
      case MessageTypes.SKETCH:
        dispatch(setFiles(payload.files));
        setBasePath(payload.basePath);
        setTextOutput(payload.textOutput);
        setGridOutput(payload.gridOutput);
        setSliderValues(payload.sliderValues || {});
        break;
      case MessageTypes.START:
        setIsPlaying(true);
        break;
      case MessageTypes.STOP:
        setIsPlaying(false);
        break;
      case MessageTypes.REGISTER:
        dispatchMessage({ type: MessageTypes.REGISTER });
        break;
      case MessageTypes.EXECUTE:
        dispatchMessage(payload);
        break;
      case MessageTypes.AR_SLIDER:
        dispatchMessage(message); // forward to the inner sketch frame
        break;
      default:
        break;
    }
  }

  function addCacheBustingToAssets(files) {
    const timestamp = new Date().getTime();
    return files.map((file) => {
      if (file.url && !file.url.endsWith('obj') && !file.url.endsWith('stl')) {
        return {
          ...file,
          url: `${file.url}?v=${timestamp}`
        };
      }
      return file;
    });
  }

  useEffect(() => {
    const unsubscribe = listen(handleMessageEvent);
    return function cleanup() {
      unsubscribe();
    };
  });
  return (
    <React.Fragment>
      <GlobalStyle />
      <EmbedFrame
        files={addCacheBustingToAssets(state)}
        isPlaying={isPlaying}
        basePath={basePath}
        gridOutput={gridOutput}
        textOutput={textOutput}
        sliderValues={sliderValues}
      />
    </React.Fragment>
  );
};

render(<App />, document.getElementById('root'));
